import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Pagination from '../../components/ui/Pagination';
import { servicesCatalogService } from '../../services/catalogService';
import { servicePartnerCountService } from '../../services/serviceProfileService';
import { slaLevelsService, certificationsService, industryAlignmentsService } from '../../services/catalogService';
import { useToast } from '../../contexts/ToastContext';
import MultiSelectDropdown from '../../components/ui/MultiSelectDropdown';

const CATEGORY_OPTIONS = [
  { value: 'design', label: 'Design' },
  { value: 'supply', label: 'Supply' },
  { value: 'installation', label: 'Installation' },
  { value: 'commissioning', label: 'Commissioning' },
  { value: 'support', label: 'Support' },
  { value: 'managed-services', label: 'Managed Services' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'training', label: 'Training' },
];

const DELIVERY_OPTIONS = [
  { value: 'partner-led', label: 'Partner-Led' },
  { value: 'joint', label: 'Joint' },
  { value: 'ecity-led', label: 'eCity-Led' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'draft', label: 'Draft' },
];

const FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'draft', label: 'Draft' },
];

const EMPTY_FORM = {
  serviceName: '',
  serviceCategory: '',
  deliveryModel: '',
  slaLevels: '',
  certificationsRequired: '',
  description: '',
  industryAlignment: '',
  catalogStatus: 'active',
};

const PAGE_SIZE = 10;

const getCategoryColor = (cat) => {
  const map = {
    design: 'bg-violet-100 text-violet-700',
    supply: 'bg-blue-100 text-blue-700',
    installation: 'bg-cyan-100 text-cyan-700',
    commissioning: 'bg-teal-100 text-teal-700',
    support: 'bg-amber-100 text-amber-700',
    'managed-services': 'bg-indigo-100 text-indigo-700',
    consulting: 'bg-pink-100 text-pink-700',
    training: 'bg-green-100 text-green-700',
  };
  return map?.[cat] || 'bg-slate-100 text-slate-600';
};

const getStatusColor = (status) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    sunset: 'bg-amber-100 text-amber-700',
    draft: 'bg-slate-100 text-slate-600',
  };
  return map?.[status] || 'bg-slate-100 text-slate-600';
};

const getDeliveryColor = (model) => {
  const map = {
    'partner-led': 'bg-blue-50 text-blue-600',
    joint: 'bg-purple-50 text-purple-600',
    'ecity-led': 'bg-green-50 text-green-600',
  };
  return map?.[model] || 'bg-slate-50 text-slate-600';
};

// SLA compliance color based on percentage
const getSlaComplianceColor = (pct) => {
  if (pct === null || pct === undefined) return { dot: 'bg-slate-300', text: 'text-slate-400', label: '—' };
  if (pct >= 95) return { dot: 'bg-emerald-500', text: 'text-emerald-600', label: `${pct}%` };
  if (pct >= 80) return { dot: 'bg-amber-400', text: 'text-amber-600', label: `${pct}%` };
  return { dot: 'bg-red-500', text: 'text-red-600', label: `${pct}%` };
};

// Deterministic mock SLA compliance per service (based on id hash)
const getMockSlaCompliance = (serviceId) => {
  if (!serviceId) return null;
  const hash = serviceId?.split('')?.reduce((acc, c) => acc + c?.charCodeAt(0), 0);
  const values = [98, 94, 87, 76, 99, 91, 83, 72, 96, 88];
  return values?.[hash % values?.length];
};

// Coverage regions mock data
const REGIONS = ['NA', 'EU', 'APAC', 'MEA', 'LATAM'];
const getMockCoverage = (serviceId) => {
  if (!serviceId) return [];
  const hash = serviceId?.split('')?.reduce((acc, c) => acc + c?.charCodeAt(0), 0);
  return REGIONS?.filter((_, i) => (hash >> i) & 1);
};

export default function ServicesCatalog() {
  const toast = useToast();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [partnerCounts, setPartnerCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [partnerPopover, setPartnerPopover] = useState(null);
  const [certTooltip, setCertTooltip] = useState(null);
  const [coveragePopover, setCoveragePopover] = useState(null);
  const popoverRef = useRef(null);

  // Reference data for dropdowns
  const [slaOptions, setSlaOptions] = useState([]);
  const [certOptions, setCertOptions] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [refLoading, setRefLoading] = useState(true);

  useEffect(() => {
    loadServices();
    loadReferenceData();
  }, []);

  // Close popovers on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e?.target?.closest('[data-popover]')) {
        setPartnerPopover(null);
        setCoveragePopover(null);
      }
    };
    document?.addEventListener('mousedown', handler);
    return () => document?.removeEventListener('mousedown', handler);
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const [data, counts] = await Promise.all([
        servicesCatalogService?.getAll(),
        servicePartnerCountService?.getAllCounts(),
      ]);
      setServices(data);
      setPartnerCounts(counts || {});
    } catch (err) {
      toast?.error('Failed to load services catalog.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadReferenceData = async () => {
    setRefLoading(true);
    try {
      const [sla, certs, industries] = await Promise.all([
        slaLevelsService?.getAll(),
        certificationsService?.getAll(),
        industryAlignmentsService?.getAll(),
      ]);
      setSlaOptions(sla || []);
      setCertOptions(certs || []);
      setIndustryOptions(industries || []);
    } catch (err) {
      console.error('Failed to load reference data:', err);
    } finally {
      setRefLoading(false);
    }
  };

  const handleField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!formData?.serviceName?.trim()) {
      toast?.error('Service name is required.');
      return;
    }
    try {
      if (editingId) {
        const updated = await servicesCatalogService?.update(editingId, formData);
        setServices(prev => prev?.map(s => s?.id === editingId ? updated : s));
        toast?.success('Service updated in catalog.');
      } else {
        const created = await servicesCatalogService?.create(formData);
        setServices(prev => [created, ...prev]);
        toast?.success('Service added to catalog.');
      }
      resetForm();
    } catch (err) {
      toast?.error(editingId ? 'Failed to update service.' : 'Failed to add service.');
      console.error(err);
    }
  };

  const handleEdit = (service) => {
    setFormData({
      serviceName: service?.serviceName || '',
      serviceCategory: service?.serviceCategory || '',
      deliveryModel: service?.deliveryModel || '',
      slaLevels: service?.slaLevels || '',
      certificationsRequired: service?.certificationsRequired || '',
      description: service?.description || '',
      industryAlignment: service?.industryAlignment || '',
      catalogStatus: service?.catalogStatus || 'active',
    });
    setEditingId(service?.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    try {
      await servicesCatalogService?.delete(id);
      setServices(prev => prev?.filter(s => s?.id !== id));
      setSelectedIds(prev => prev?.filter(sid => sid !== id));
      toast?.success('Service removed from catalog.');
    } catch (err) {
      toast?.error('Failed to delete service.');
      console.error(err);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleClone = async (service) => {
    try {
      const cloned = await servicesCatalogService?.create({
        ...service,
        serviceName: `${service?.serviceName} (Copy)`,
        catalogStatus: 'draft',
      });
      setServices(prev => [cloned, ...prev]);
      toast?.success('Service cloned successfully.');
    } catch (err) {
      toast?.error('Failed to clone service.');
      console.error(err);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds?.length === 0) return;
    const statusMap = { 'set-active': 'active', 'set-sunset': 'sunset', 'set-draft': 'draft' };
    const newStatus = statusMap?.[bulkAction];
    if (!newStatus) return;
    try {
      await Promise.all(
        selectedIds?.map(id => {
          const svc = services?.find(s => s?.id === id);
          return svc ? servicesCatalogService?.update(id, { ...svc, catalogStatus: newStatus }) : Promise.resolve();
        })
      );
      setServices(prev => prev?.map(s => selectedIds?.includes(s?.id) ? { ...s, catalogStatus: newStatus } : s));
      toast?.success(`${selectedIds?.length} service(s) updated to ${newStatus}.`);
      setSelectedIds([]);
      setBulkAction('');
    } catch (err) {
      toast?.error('Bulk update failed.');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const filtered = services?.filter(s => {
    const matchSearch = !search ||
      s?.serviceName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      s?.industryAlignment?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = !statusFilter || s?.catalogStatus === statusFilter;
    const matchCategory = !categoryFilter || s?.serviceCategory === categoryFilter;
    const matchDelivery = !deliveryFilter || s?.deliveryModel === deliveryFilter;
    return matchSearch && matchStatus && matchCategory && matchDelivery;
  });

  const paginated = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageIds = paginated?.map(s => s?.id);
  const allPageSelected = pageIds?.length > 0 && pageIds?.every(id => selectedIds?.includes(id));

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const allSelected = pageIds?.every(id => selectedIds?.includes(id));
    setSelectedIds(allSelected ? selectedIds?.filter(id => !pageIds?.includes(id)) : [...new Set([...selectedIds, ...pageIds])]);
  };

  return (
    <>
      <Helmet>
        <title>Services Catalog - PartnerHub</title>
        <meta name="description" content="Master services catalog for partner portfolio mapping" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/20 to-cyan-50/20">
        <Header />

        <main className="pt-[116px] pb-32 px-24 lg:px-32">
          <div className="max-w-[1600px] mx-auto">

            {/* Page Header */}
            <div className="mb-32">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-20 mb-24">
                <div className="flex items-center gap-16">
                  <div className="p-12 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-xl shadow-lg">
                    <Icon name="Wrench" size={28} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900">Services Catalog</h1>
                    <p className="text-slate-500 mt-4">Master catalog — partners select from this list</p>
                  </div>
                </div>
                <Button
                  variant={showForm ? 'outline' : 'default'}
                  iconName={showForm ? 'X' : 'Plus'}
                  iconPosition="left"
                  iconSize={18}
                  onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}
                >
                  {showForm ? 'Cancel' : 'Add Service'}
                </Button>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-16 mb-24">
                {[
                  { label: 'Total Services', value: services?.length, icon: 'Wrench', color: 'text-teal-600' },
                  { label: 'Active', value: services?.filter(s => s?.catalogStatus === 'active')?.length, icon: 'CheckCircle2', color: 'text-emerald-600' },
                  { label: 'Sunset', value: services?.filter(s => s?.catalogStatus === 'sunset')?.length, icon: 'Sunset', color: 'text-amber-600' },
                  { label: 'Draft', value: services?.filter(s => s?.catalogStatus === 'draft')?.length, icon: 'FileEdit', color: 'text-slate-500' },
                ]?.map(stat => (
                  <div key={stat?.label} className="bg-white rounded-xl border border-slate-200 p-16 shadow-sm">
                    <div className="flex items-center gap-10 mb-8">
                      <Icon name={stat?.icon} size={16} className={stat?.color} />
                      <span className="text-xs text-slate-500 font-medium">{stat?.label}</span>
                    </div>
                    <p className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Add / Edit Form */}
            {showForm && (
              <div className="bg-white border border-slate-200 rounded-2xl p-24 shadow-sm mb-32">
                <h3 className="text-lg font-semibold text-slate-900 mb-20">
                  {editingId ? 'Edit Service' : 'Add New Service to Catalog'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                  <Input
                    label="Service Name *"
                    placeholder="e.g., Managed Security Operations"
                    value={formData?.serviceName}
                    onChange={e => handleField('serviceName', e?.target?.value)}
                  />
                  <Select
                    label="Category"
                    options={CATEGORY_OPTIONS}
                    value={formData?.serviceCategory}
                    onChange={v => handleField('serviceCategory', v)}
                    placeholder="Select category"
                  />
                  <Select
                    label="Delivery Model"
                    options={DELIVERY_OPTIONS}
                    value={formData?.deliveryModel}
                    onChange={v => handleField('deliveryModel', v)}
                    placeholder="Select delivery model"
                  />
                  <MultiSelectDropdown
                    label="SLA Levels"
                    options={slaOptions}
                    value={formData?.slaLevels}
                    onChange={v => handleField('slaLevels', v)}
                    placeholder="Select SLA levels..."
                    loading={refLoading}
                  />
                  <MultiSelectDropdown
                    label="Certifications Required"
                    options={certOptions}
                    value={formData?.certificationsRequired}
                    onChange={v => handleField('certificationsRequired', v)}
                    placeholder="Select certifications..."
                    loading={refLoading}
                  />
                  <MultiSelectDropdown
                    label="Industry Alignment"
                    options={industryOptions}
                    value={formData?.industryAlignment}
                    onChange={v => handleField('industryAlignment', v)}
                    placeholder="Select industries..."
                    loading={refLoading}
                  />
                  <Select
                    label="Status"
                    options={STATUS_OPTIONS}
                    value={formData?.catalogStatus}
                    onChange={v => handleField('catalogStatus', v)}
                  />
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-6">Description</label>
                    <textarea
                      className="w-full border border-slate-200 rounded-lg px-12 py-10 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                      rows={3}
                      placeholder="Brief service description..."
                      value={formData?.description}
                      onChange={e => handleField('description', e?.target?.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-12 mt-20">
                  <Button variant="default" onClick={handleSubmit}>
                    {editingId ? 'Update Service' : 'Add to Catalog'}
                  </Button>
                  <Button variant="outline" onClick={resetForm}>Cancel</Button>
                </div>
              </div>
            )}

            {/* ── Category Filter Tabs ── */}
            <div className="flex flex-wrap items-center gap-8 mb-16">
              {[{ value: '', label: 'All' }, ...CATEGORY_OPTIONS]?.map(cat => (
                <button
                  key={cat?.value}
                  onClick={() => { setCategoryFilter(cat?.value); setPage(1); }}
                  className={`px-14 py-7 rounded-full text-xs font-semibold transition-colors ${
                    categoryFilter === cat?.value
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-teal-300 hover:text-teal-700'
                  }`}
                >
                  {cat?.label}
                </button>
              ))}
            </div>

            {/* ── Delivery Model Toggles ── */}
            <div className="flex items-center gap-8 mb-20">
              <span className="text-xs font-medium text-slate-500 mr-4">Delivery:</span>
              {[{ value: '', label: 'All' }, ...DELIVERY_OPTIONS]?.map(d => (
                <button
                  key={d?.value}
                  onClick={() => { setDeliveryFilter(d?.value); setPage(1); }}
                  className={`px-12 py-6 rounded-lg text-xs font-semibold border transition-colors ${
                    deliveryFilter === d?.value
                      ? 'bg-slate-800 text-white border-slate-800' :'bg-white border-slate-200 text-slate-600 hover:border-slate-400'
                  }`}
                >
                  {d?.label}
                </button>
              ))}
            </div>

            {/* Filters Bar */}
            <div className="flex flex-col sm:flex-row gap-12 mb-16">
              <div className="relative flex-1 max-w-sm">
                <Icon name="Search" size={16} className="absolute left-12 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="w-full pl-36 pr-12 py-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
                  placeholder="Search services..."
                  value={search}
                  onChange={e => { setSearch(e?.target?.value); setPage(1); }}
                />
              </div>
              <div className="w-48">
                <Select
                  options={FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={v => { setStatusFilter(v); setPage(1); }}
                  placeholder="All Status"
                />
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedIds?.length > 0 && (
              <div className="flex items-center gap-12 mb-16 px-16 py-12 bg-teal-50 border border-teal-200 rounded-xl">
                <Icon name="CheckSquare" size={16} className="text-teal-600" />
                <span className="text-sm font-medium text-teal-800">{selectedIds?.length} selected</span>
                <div className="flex items-center gap-8 ml-auto">
                  <select
                    className="border border-teal-300 rounded-lg px-10 py-7 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400/30"
                    value={bulkAction}
                    onChange={e => setBulkAction(e?.target?.value)}
                  >
                    <option value="">Bulk Action...</option>
                    <option value="set-active">Set Active</option>
                    <option value="set-sunset">Set Sunset</option>
                    <option value="set-draft">Set Draft</option>
                  </select>
                  <button
                    onClick={handleBulkAction}
                    disabled={!bulkAction}
                    className="px-14 py-7 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-40 transition-colors"
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => setSelectedIds([])}
                    className="px-12 py-7 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              {loading ? (
                <div className="flex items-center justify-center py-64">
                  <div className="flex items-center gap-12 text-slate-500">
                    <Icon name="Loader2" size={20} className="animate-spin" />
                    <span>Loading catalog...</span>
                  </div>
                </div>
              ) : paginated?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-64 text-slate-400">
                  <Icon name="Wrench" size={40} className="mb-12 opacity-30" />
                  <p className="font-medium">No services found</p>
                  <p className="text-sm mt-4">Add the first service to the master catalog</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-16 py-12 text-left w-10">
                          <input
                            type="checkbox"
                            checked={allPageSelected}
                            onChange={toggleSelectAll}
                            className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                          />
                        </th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Service Name</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Category</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Delivery Model</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Partners</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">SLA Compliance</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Coverage</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                        <th className="px-16 py-12 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {paginated?.map(service => {
                        const slaCompliance = getMockSlaCompliance(service?.id);
                        const slaMeta = getSlaComplianceColor(slaCompliance);
                        const coverage = getMockCoverage(service?.id);
                        const partnerCount = partnerCounts?.[service?.id] || 0;
                        const hasCerts = !!service?.certificationsRequired;

                        return (
                          <tr
                            key={service?.id}
                            className="hover:bg-slate-50/60 transition-colors cursor-pointer"
                            onClick={(e) => {
                              if (e?.target?.closest('[data-no-nav]')) return;
                              navigate(`/services-catalog/${service?.id}`);
                            }}
                          >
                            <td className="px-16 py-14" data-no-nav>
                              <input
                                type="checkbox"
                                checked={selectedIds?.includes(service?.id)}
                                onChange={() => toggleSelect(service?.id)}
                                onClick={e => e?.stopPropagation()}
                                className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                              />
                            </td>
                            <td className="px-16 py-14">
                              <div className="flex items-start gap-8">
                                <div>
                                  <div className="flex items-center gap-8">
                                    <p className="text-sm font-semibold text-slate-800 hover:text-teal-700 transition-colors">
                                      {service?.serviceName}
                                    </p>
                                    {/* Certification Badge */}
                                    {hasCerts && (
                                      <div className="relative" data-no-nav>
                                        <button
                                          onMouseEnter={() => setCertTooltip(service?.id)}
                                          onMouseLeave={() => setCertTooltip(null)}
                                          className="flex items-center justify-center w-18 h-18 rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors flex-shrink-0"
                                          title="Certifications required"
                                        >
                                          <Icon name="Award" size={11} />
                                        </button>
                                        {certTooltip === service?.id && (
                                          <div className="absolute left-0 top-full mt-6 z-20 bg-slate-900 text-white text-xs rounded-lg px-12 py-8 shadow-xl whitespace-nowrap min-w-[160px]">
                                            <p className="font-semibold mb-4">Certs Required:</p>
                                            <p className="text-slate-300">{service?.certificationsRequired}</p>
                                            <div className="absolute -top-4 left-8 w-8 h-8 bg-slate-900 rotate-45" />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {service?.description && (
                                    <p className="text-xs text-slate-400 mt-2 line-clamp-1">{service?.description}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-16 py-14">
                              <span className={`inline-flex px-8 py-4 rounded-md text-xs font-medium ${getCategoryColor(service?.serviceCategory)}`}>
                                {CATEGORY_OPTIONS?.find(c => c?.value === service?.serviceCategory)?.label || service?.serviceCategory}
                              </span>
                            </td>
                            <td className="px-16 py-14">
                              <span className={`inline-flex px-8 py-4 rounded-md text-xs font-medium ${getDeliveryColor(service?.deliveryModel)}`}>
                                {DELIVERY_OPTIONS?.find(d => d?.value === service?.deliveryModel)?.label || service?.deliveryModel || '—'}
                              </span>
                            </td>
                            {/* Partner Count Column */}
                            <td className="px-16 py-14" data-no-nav>
                              <div className="relative" data-popover>
                                <button
                                  onClick={(e) => {
                                    e?.stopPropagation();
                                    setPartnerPopover(partnerPopover === service?.id ? null : service?.id);
                                    setCoveragePopover(null);
                                  }}
                                  className={`flex items-center gap-6 px-10 py-5 rounded-lg text-xs font-semibold transition-colors ${
                                    partnerCount > 0
                                      ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' :'bg-slate-50 text-slate-400'
                                  }`}
                                >
                                  <Icon name="Users" size={12} />
                                  {partnerCount}
                                </button>
                                {partnerPopover === service?.id && (
                                  <div className="absolute left-0 top-full mt-6 z-20 bg-white border border-slate-200 rounded-xl shadow-xl p-14 min-w-[200px]">
                                    <p className="text-xs font-semibold text-slate-700 mb-8">
                                      {partnerCount > 0 ? `${partnerCount} partner(s) offering this service` : 'No partners linked yet'}
                                    </p>
                                    <button
                                      onClick={(e) => { e?.stopPropagation(); navigate(`/services-catalog/${service?.id}`); }}
                                      className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                                    >
                                      View all partners →
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                            {/* SLA Compliance */}
                            <td className="px-16 py-14">
                              <div className="flex items-center gap-6">
                                <span className={`w-8 h-8 rounded-full flex-shrink-0 ${slaMeta?.dot}`} />
                                <span className={`text-xs font-semibold ${slaMeta?.text}`}>{slaMeta?.label}</span>
                              </div>
                            </td>
                            {/* Coverage Heatmap */}
                            <td className="px-16 py-14" data-no-nav>
                              <div className="relative" data-popover>
                                <button
                                  onClick={(e) => {
                                    e?.stopPropagation();
                                    setCoveragePopover(coveragePopover === service?.id ? null : service?.id);
                                    setPartnerPopover(null);
                                  }}
                                  className="flex items-center gap-4"
                                >
                                  {REGIONS?.map(region => (
                                    <span
                                      key={region}
                                      className={`text-[9px] font-bold px-5 py-2 rounded ${
                                        coverage?.includes(region)
                                          ? 'bg-teal-500 text-white' :'bg-slate-100 text-slate-300'
                                      }`}
                                    >
                                      {region}
                                    </span>
                                  ))}
                                </button>
                                {coveragePopover === service?.id && (
                                  <div className="absolute left-0 top-full mt-6 z-20 bg-white border border-slate-200 rounded-xl shadow-xl p-14 min-w-[220px]">
                                    <p className="text-xs font-semibold text-slate-700 mb-10">Coverage Heatmap</p>
                                    <div className="space-y-6">
                                      {REGIONS?.map(region => (
                                        <div key={region} className="flex items-center justify-between gap-12">
                                          <span className="text-xs text-slate-600">{region}</span>
                                          <div className={`h-6 rounded-full flex-1 ${coverage?.includes(region) ? 'bg-teal-400' : 'bg-slate-100'}`} />
                                          <span className={`text-xs font-medium ${coverage?.includes(region) ? 'text-teal-600' : 'text-slate-300'}`}>
                                            {coverage?.includes(region) ? 'Covered' : 'Gap'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-16 py-14">
                              <span className={`inline-flex px-8 py-4 rounded-full text-xs font-medium capitalize ${getStatusColor(service?.catalogStatus)}`}>
                                {service?.catalogStatus}
                              </span>
                            </td>
                            <td className="px-16 py-14" data-no-nav>
                              <div className="flex items-center justify-end gap-4">
                                {/* Clone */}
                                <button
                                  onClick={(e) => { e?.stopPropagation(); handleClone(service); }}
                                  className="p-8 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                                  title="Clone service"
                                >
                                  <Icon name="Copy" size={15} />
                                </button>
                                {/* Edit */}
                                <button
                                  onClick={(e) => { e?.stopPropagation(); handleEdit(service); }}
                                  className="p-8 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/10 transition-colors"
                                  title="Edit"
                                >
                                  <Icon name="Edit" size={15} />
                                </button>
                                {/* Delete */}
                                {deleteConfirm === service?.id ? (
                                  <div className="flex items-center gap-4">
                                    <button
                                      onClick={(e) => { e?.stopPropagation(); handleDelete(service?.id); }}
                                      className="px-8 py-4 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
                                    >
                                      Confirm
                                    </button>
                                    <button
                                      onClick={(e) => { e?.stopPropagation(); setDeleteConfirm(null); }}
                                      className="px-8 py-4 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => { e?.stopPropagation(); setDeleteConfirm(service?.id); }}
                                    className="p-8 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                    title="Delete"
                                  >
                                    <Icon name="Trash2" size={15} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination */}
            {filtered?.length > PAGE_SIZE && (
              <div className="mt-20">
                <Pagination
                  currentPage={page}
                  totalItems={filtered?.length}
                  itemsPerPage={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
}
